import parse from 'html-react-parser';
import type { ReactNode } from 'react';
import React, { cloneElement } from 'react';
import styled from 'styled-components';
import type { TextLinkProps, TextListProps } from '@oneaudi/unified-web-components';
import { TextLink, TextList, TextListItem } from '@oneaudi/unified-web-components';
import { FilterXSS } from 'xss';
import {
  CMP_COLOR_NAVIGATION_TEXT_LINK_FOREGROUND_DEFAULT_DARK,
  SYS_SPACE_FIXED_300,
} from '@oneaudi/unified-web-common';
import { renderReactNodeWithFootnotes } from '../../utils/renderReactNodeWithFootnotes';

const StyledList = styled(TextList)`
  margin-inline-start: var(--relative-spacing-md);
  margin-top: ${SYS_SPACE_FIXED_300};
`;

const StyledTextLink = styled(TextLink)`
  ${!process.env.IS_LEGACY &&
  `color: ${CMP_COLOR_NAVIGATION_TEXT_LINK_FOREGROUND_DEFAULT_DARK};
  `}/* (temp) the color gets overriden with color: iherit  */
`;

type TextListPropsWithoutChildren = Omit<TextListProps, 'children'>;
type TextLinkPropsWithoutChildren = Omit<TextLinkProps, 'children'>;

const createElement = (
  type: string,
  props?: object,
  ...children: ReactNode[]
): React.JSX.Element => {
  if (type === 'br') {
    return React.createElement(type, props);
  }
  const renderChildren = () => renderReactNodeWithFootnotes(children);
  if (type === 'a') {
    const enhandedProps: TextLinkPropsWithoutChildren = {
      ...(props as TextLinkPropsWithoutChildren),
      size: 'md',
      variant: 'inline',
    };
    return React.createElement(StyledTextLink, enhandedProps, renderChildren());
  }
  if (type === 'ul' || type === 'ol') {
    const variant = type === 'ul' ? 'bullet' : 'decimal';
    const enhancedProps: TextListPropsWithoutChildren = {
      ...(props as TextListPropsWithoutChildren),
      size: 'md',
      variant,
      className: 'list',
    };
    return React.createElement(StyledList, enhancedProps, renderChildren());
  }
  if (type === 'li') {
    return React.createElement(TextListItem, props, renderChildren());
  }
  return React.createElement(type, props, renderChildren());
};
const sanitizer = new FilterXSS({
  whiteList: {
    b: [],
    i: [],
    u: [],
    ul: [],
    ol: [],
    li: [],
    br: [],
    p: [],
    a: ['href'],
    strong: [],
  },
  stripIgnoreTagBody: ['script'],
  onIgnoreTag: () => '',
});

export const sanitizeRichText = (htmlString: string): string => sanitizer.process(htmlString);

export function convertRichText(htmlString: string) {
  const sanitizedHtmlString = sanitizeRichText(htmlString);

  return parse(sanitizedHtmlString, {
    library: {
      cloneElement,
      createElement,
      isValidElement: (element: unknown): boolean => Boolean(element),
    },
  });
}
